// Full-screen, non-dismissable auth gate. Deliberately NOT an AppModal/
// react-bootstrap Modal — this must be able to render on top of an already-
// open AppModal (or a crashed render tree) without fighting its backdrop/
// z-index, so it's a plain fixed-position overlay instead. See the
// "no popup over a popup" house rule in CLAUDE.md — this is the sanctioned
// full-screen exception.
import React from "react";
import i18n from "i18next";
import { redirectToAuth } from "../../../common/environment_config.es6";
import "./_authRequiredModal.scss";

const handleAuthenticate = () => {
  redirectToAuth();
};

const handleHardRefresh = () => {
  // True cache-bypass reload isn't reliably scriptable in modern browsers;
  // reload() is the practical ceiling.
  window.location.reload();
};

const AuthRequiredModal = ({ reason, errorText, onDismiss }) => {
  const isLoop = reason === "loop";
  const isServerError = reason === "serverError";
  const isCrash = reason === "crash";

  return (
    <div className="auth-required-modal">
      <div className="auth-required-modal__card">
        <div className="auth-required-modal__title">
          {isCrash
            ? i18n.t("authRequired.crashTitle", { defaultValue: "The editor hit an error" })
            : isServerError
              ? i18n.t("authRequired.serverErrorTitle", {
                  defaultValue: "Couldn't load the editor",
                })
              : i18n.t("authRequired.title", { defaultValue: "You need to sign in again" })}
        </div>
        <div className="auth-required-modal__description">
          {isCrash
            ? i18n.t("authRequired.crashDescription", {
                defaultValue:
                  "A rendering error occurred — this is not a sign-in problem. Refresh to try again; if it keeps happening, share the message below.",
              })
            : isServerError
              ? i18n.t("authRequired.serverErrorDescription", {
                  defaultValue: "The server is temporarily unavailable. Refresh to try again.",
                })
              : isLoop
                ? i18n.t("authRequired.loopDescription", {
                    defaultValue:
                      "Too many sign-in attempts — please refresh or try again shortly.",
                  })
                : i18n.t("authRequired.description", {
                    defaultValue:
                      "Your session has expired or could not be verified. Sign in again to continue.",
                  })}
        </div>
        {isCrash && errorText && (
          <div className="auth-required-modal__error-detail">{errorText}</div>
        )}
        <div className="auth-required-modal__actions">
          {!isLoop && !isServerError && !isCrash && (
            <button
              type="button"
              className="auth-required-modal__btn auth-required-modal__btn--primary"
              onClick={handleAuthenticate}
            >
              {i18n.t("authRequired.authenticate", { defaultValue: "Authenticate" })}
            </button>
          )}
          <button
            type="button"
            className="auth-required-modal__btn auth-required-modal__btn--ghost"
            onClick={handleHardRefresh}
          >
            {i18n.t("authRequired.hardRefresh", { defaultValue: "Hard refresh" })}
          </button>
          {onDismiss && (
            <button
              type="button"
              className="auth-required-modal__btn auth-required-modal__btn--ghost"
              onClick={onDismiss}
            >
              {i18n.t("authRequired.goBack", { defaultValue: "Go back to the training" })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthRequiredModal;
